import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { LoginStateService } from '@app/features/auth/services/login.state.service';
import { Page } from '@app/shared/models/page.interface';
import { SearchParams } from '@app/shared/models/search-params.model';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { InjectableRxStompConfig, RxStompService } from '@stomp/ng2-stompjs';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { Notification } from '../model/notification.model';

/**
 * Stub for the configuration of the {@link RxStompService}. This configuration
 * will be enriched with the dynamic brokerURL and Authentication header when they
 * will be know at runtime.
 */
const STOMP_CONFIG_STUB: InjectableRxStompConfig = {
  // Which server?
  brokerURL: '',

  // Headers
  // Typical keys: login, passcode, host
  connectHeaders: {
    login: 'guest',
    passcode: 'guest'
  },

  // How often to heartbeat?
  // Interval in milliseconds, set to 0 to disable
  heartbeatIncoming: 0, // Typical value 0 - disabled
  heartbeatOutgoing: 20000, // Typical value 20000 - every 20 seconds

  // Wait in milliseconds before attempting auto reconnect
  // Set to 0 to disable
  // Typical value 5000 (5 seconds)
  reconnectDelay: 5000,

  // Will log diagnostics on console
  debug: (msg: string): void => {
    console.log(new Date(), msg);
  }
};

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  protected notifications$ = new Subject<Notification>();
  protected numUnreadNotifications$ = new BehaviorSubject<number>(0);
  protected socketSubscription: Subscription;
  protected stompService: RxStompService;

  constructor(
    protected appConfigService: AppConfigService,
    @Inject('LoginStateService') protected loginStateService: LoginStateService,
    protected http: HttpClient
  ) {
    this.stompService = new RxStompService();
  }

  openSocket(): void {
    // FIXME: uncomment this when WebSocket problem is solved.

    // Configure and activate the actual websocket service
    /*const config: InjectableRxStompConfig = this.prepareConfiguration();
    this.stompService.configure(config);
    this.stompService.activate();

    this.socketSubscription = this.stompService
      .watch('/user/queue/user-notifications')
      .pipe(
        tap((message: Message) => {
          const notification: Notification = JSON.parse(message.body) as Notification;
          this.notifications$.next(notification);

          this.updateNumUnreadNotifications(1);
        })
      )
      .subscribe();*/

    this.refreshNumUnreadNotifications();
  }

  refreshNumUnreadNotifications(): void {
    this.http
      .get<number>(`${this.appConfigService.getApi()}/notifications/unread/count`)
      .subscribe((numUnreadNotifications: number) => this.numUnreadNotifications$.next(numUnreadNotifications));
  }

  setNumUnreadNotifications(value: number): void {
    this.numUnreadNotifications$.next(value);
  }

  updateNumUnreadNotifications(increment: number): void {
    this.numUnreadNotifications$.next(this.numUnreadNotifications$.getValue() + increment);
  }

  reduceNumUnreadNotifications(value: number): void {
    this.numUnreadNotifications$.next(this.numUnreadNotifications$.getValue() - value);
  }

  closeSocket(): void {
    if (this.stompService.active) {
      this.stompService.deactivate();
    }

    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
    }
  }

  protected prepareConfiguration() {
    const headers = {
      Authentication: this.loginStateService.getToken()
    };

    const config: InjectableRxStompConfig = {
      ...STOMP_CONFIG_STUB,
      brokerURL: this.appConfigService.getNotificationsWebSocket(),
      connectHeaders: headers
    };

    return config;
  }

  getLiveNotifications(): Observable<Notification> {
    return this.notifications$.asObservable();
  }

  getNumUnreadNotifications(): Observable<number> {
    return this.numUnreadNotifications$.asObservable();
  }

  getAllPaginated(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string
  ): Observable<Page<Notification>> {
    const params: SearchParams = {
      search,
      filter,
      pageNumber: pageState.index,
      pageSize: pageState.size,
      sortDirection: sortState.direction.toString().toUpperCase(),
      sortField: sortState.column
    };
    return this.http.post<Page<Notification>>(`${this.appConfigService.getApi()}/notifications/search`, params);
  }

  markAllAsRead(): Observable<void> {
    return this.http.patch<void>(`${this.appConfigService.getApi()}/notifications/read`, {});
  }

  markAsRead(id: string, read: boolean): Observable<void> {
    return this.http.patch<void>(`${this.appConfigService.getApi()}/notifications/read/${id}/${read}`, {});
  }

  markListAsRead(ids: string[], read: boolean): Observable<void> {
    return this.http.patch<void>(`${this.appConfigService.getApi()}/notifications/read/${read}`, ids);
  }

  markAsFlagged(id: string, flagged: boolean): Observable<void> {
    return this.http.patch<void>(`${this.appConfigService.getApi()}/notifications/flag/${id}/${flagged}`, {});
  }

  markListAsFlagged(ids: string[], flagged: boolean): Observable<void> {
    return this.http.patch<void>(`${this.appConfigService.getApi()}/notifications/flag/${flagged}`, ids);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.appConfigService.getApi()}/notifications/${id}`);
  }

  deleteList(ids: string[]): Observable<void> {
    return this.http.post<void>(`${this.appConfigService.getApi()}/notifications/delete`, ids);
  }
}
